import { action, computed, observable } from 'mobx';
import { missingRequiredPartnerFields, PartnerInfo, readPartnerIdentity } from '../models/partnerInfo/PartnerInfo';
import { PartnerApiService, PartnerServiceUnavailableError } from '../services/PartnerApiService';

/**
 * How the configurator was opened. Read from the session parameters rather than from the resolved
 * partner data: a shop session answers with a contact that carries a Kundennummer of its own, so the
 * data cannot tell the two embeddings apart.
 */
export enum PartnerContext {
  /** tuerenkonfigurator.joka.de — nobody is logged in. */
  Standalone = 'standalone',
  /** A logged-in dealer inside the JOKA shop (B2B). */
  ShopSession = 'shop-session',
  /** A dealer embedding the configurator for their own end customers (B2C). */
  DealerEmbedding = 'dealer-embedding'
}

/** Why a dealer embedding cannot be served — each maps to its own message on the setup error page. */
export enum PartnerSetupError {
  /** The Kundennummer is not known to the JOKA shop (API #1 answered 404). */
  UnknownCustomer = 'unknown-customer',
  /** The dealer exists but required fields are not maintained in the shop. */
  IncompleteData = 'incomplete-data',
  /** API #1 could not be reached — a temporary fault, not a configuration mistake. */
  ServiceUnavailable = 'service-unavailable'
}

/**
 * Holds the dealer/partner data for the running session (JOKA-305).
 *
 * Lives in `shared` rather than `core` because the check has to run before any route renders — a dealer
 * embeds the configurator on their own page and lands on the catalog, not on the cart.
 */
export class PartnerStore {
  private readonly api = new PartnerApiService();

  @observable info: PartnerInfo = {};
  @observable context: PartnerContext = PartnerContext.Standalone;
  @observable error: PartnerSetupError | null = null;
  /** Which required fields the dealer has to fill in — only meaningful for `IncompleteData`. */
  @observable missingFields: (keyof PartnerInfo)[] = [];

  @computed get hasSetupError(): boolean {
    return this.error !== null;
  }

  /**
   * The guide video is JOKA's own material: it plays for a logged-in dealer and on JOKA's public site,
   * but stays out of an embedding a dealer serves to their own end customers (JOKA-337).
   */
  @computed get isDealerEmbedding(): boolean {
    return this.context === PartnerContext.DealerEmbedding;
  }

  @computed get kundennummer(): string {
    return this.info.kundennummer ?? '';
  }

  /**
   * Resolves the partner from the session parameters. B2B (a logged-in dealer) takes its contact from the
   * shop session; B2C looks the dealer up by the Kundennummer from the iframe URL.
   *
   * Only the B2C path can raise a setup error: it is the dealer's own embedding, so surfacing the problem
   * is what makes them fix it in the shop. A logged-in shop session degrades quietly instead — the dealer
   * is already inside the shop and an expired token must not lock them out of the configurator.
   */
  @action.bound
  async load(params: URLSearchParams) {
    this.info = readPartnerIdentity(params);
    const contextToken = params.get('context-token')?.trim();

    if (contextToken) {
      this.setContext(PartnerContext.ShopSession);
      await this.loadShopContact(contextToken);
      return;
    }
    if (this.info.kundennummer) {
      this.setContext(PartnerContext.DealerEmbedding);
      await this.loadDealer(this.info.kundennummer);
    }
  }

  private async loadShopContact(contextToken: string) {
    try {
      const contact = await this.api.fetchShopContact(contextToken);
      if (contact) {
        this.setInfo(contact);
      } else {
        console.warn('No dealer contact for this shop session (token expired or unknown)');
      }
    } catch (e) {
      console.warn(`Unable to load the shop contact. Suppressed error: ${(e as Error).message}`);
    }
  }

  private async loadDealer(kundennummer: string) {
    let dealer: PartnerInfo | undefined;
    try {
      dealer = await this.api.fetchDealerByKundennummer(kundennummer);
    } catch (e) {
      if (e instanceof PartnerServiceUnavailableError) {
        console.error(e.message);
        this.setError(PartnerSetupError.ServiceUnavailable);
        return;
      }
      throw e;
    }

    if (!dealer) {
      this.setError(PartnerSetupError.UnknownCustomer);
      return;
    }

    // Keep the number from the URL if the API omits it — the dealer form pre-fill relies on it.
    this.setInfo({ kundennummer, ...dealer });

    const missing = missingRequiredPartnerFields(this.info);
    if (missing.length) {
      this.setError(PartnerSetupError.IncompleteData, missing);
    }
  }

  @action
  private setContext(context: PartnerContext) {
    this.context = context;
  }

  @action
  private setInfo(info: PartnerInfo) {
    this.info = info;
  }

  @action
  private setError(error: PartnerSetupError, missingFields: (keyof PartnerInfo)[] = []) {
    this.error = error;
    this.missingFields = missingFields;
  }
}
